import { Badge, Body1, Link, Spinner, Text } from '@fluentui/react-components';

import { useDatasetVersions } from '../../../hooks/useDatasetVersions/useDatasetVersions.js';

import type { DatasetVersionsTabProps } from './DatasetVersionsTab.interface.js';
import { useStyles } from './DatasetVersionsTab.style.js';

const STATUS_BADGE_COLOR = {
  Published: 'success',
  Draft: 'important',
  Versioned: 'subtle',
} as const;

function formatDate(iso?: string): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  });
}

export function DatasetVersionsTab({
  dataSetId,
  onViewVersionData,
}: DatasetVersionsTabProps) {
  const styles = useStyles();
  const { versions, isLoading, error } = useDatasetVersions(dataSetId);

  if (isLoading) {
    return <Spinner label="Loading versions..." />;
  }

  if (error !== null) {
    return <Body1 className={styles.errorText}>{error}</Body1>;
  }

  if (versions.length === 0) {
    return <Text className={styles.emptyState}>No versions found.</Text>;
  }

  return (
    <div className={styles.root}>
      {versions.map((v) => {
        const badgeColor = STATUS_BADGE_COLOR[v.status] ?? ('subtle' as const);
        return (
          <div key={v.version} className={styles.versionRow}>
            <Text className={styles.versionLabel}>v{v.version}</Text>
            <span className={styles.versionStatus}>
              <Badge appearance="filled" color={badgeColor}>
                {v.status}
              </Badge>
            </span>
            <Text className={styles.versionDate}>
              Created {formatDate(v.createdAt)}
            </Text>
            <Link
              className={styles.viewDataLink}
              onClick={() => onViewVersionData(v)}
            >
              View Data
            </Link>
          </div>
        );
      })}
    </div>
  );
}
